'use client';

import React, { useState, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import BrokerSectionAccordion from '@/components/authorizedBrokers/BrokerSectionAccordion';
import { CountryWithBrokers, CountryGroup, AuthorizedBrokersColorsPalette } from './types';
import { BrokerLocation } from '@/components/contacts/ContactSection';

export interface AuthorizedBrokersProps {
    brokers: BrokerLocation[];
    countryGroups: CountryGroup[];
    colorsPalette: AuthorizedBrokersColorsPalette;
    // When true, two columns start from breakpoints.md; when false (default), from 864px
    twoColumnsFromMd?: boolean;
    isGhana?: boolean;
}

export default function AuthorizedBrokers({
    brokers,
    countryGroups,
    colorsPalette,
    twoColumnsFromMd,
    isGhana = false,
}: AuthorizedBrokersProps) {
    const [expandedCountries, setExpandedCountries] = useState<Set<string>>(
        () => new Set([])
    );

    const countriesWithBrokers = useMemo<CountryWithBrokers[]>(() => {
        const brokerMap = new Map(brokers.map((broker) => [broker.id, broker]));

        // Chained over a short list; merging the passes would buy no measurable win.
        // react-doctor-disable-next-line react-doctor/js-combine-iterations
        return countryGroups
            .filter((group) => !group.ghanaOnly || isGhana)
            .map((group) => ({
                id: group.id,
                name: group.name,
                brokers: group.brokerIds
                    .map((brokerId) => brokerMap.get(brokerId))
                    .filter((broker): broker is NonNullable<typeof broker> => broker !== undefined),
            }))
            .filter((country) => country.brokers.length > 0);
    }, [brokers, countryGroups, isGhana]);

    const handleAccordionChange = (countryId: string, isExpanded: boolean) => {
        setExpandedCountries((prev) => {
            const next = new Set(prev);
            if (isExpanded) {
                next.add(countryId);
            } else {
                next.delete(countryId);
            }
            return next;
        });
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '100%', md: '1088px' },
                margin: '0 auto',
            }}
        >
            <Stack>
                {countriesWithBrokers.map((country) => (
                    <BrokerSectionAccordion
                        key={country.id}
                        country={country}
                        expanded={expandedCountries.has(country.id)}
                        onChange={handleAccordionChange}
                        colorsPalette={colorsPalette}
                        twoColumnsFromMd={twoColumnsFromMd}
                    />
                ))}
            </Stack>
        </Box>
    );
}
